/**
 * MapComponent
 * ------------
 * Ova komponenta prikazuje interaktivnu kartu koristeći Leaflet biblioteku.
 * Podržava više načina rada:
 *  - "live": prikaz trenutne GPS lokacije u stvarnom vremenu
 *  - "route history": prikaz povijesti ruta
 *  - "route details": prikaz detalja određene rute
 *  - "geofence": prikaz geozona (nije implementirano)
 *
 * Metode:
 *  - ngAfterViewInit: Inicijalizira kartu i postavlja način rada
 *  - initMap: Postavlja osnovnu kartu i sloj
 *  - updateMap: Ažurira marker i centar karte prema novim GPS podacima
 *  - drawRouteOnMap: Crta rutu na karti prema zadanim točkama
 */
import { Component, AfterViewInit, inject, input, signal, ViewChild, OnDestroy } from '@angular/core';
import * as L from 'leaflet';
import { WebsocketService } from '../../modules/home/websocket/websocket.service';
import { Gps } from '@app/shared/models/ride-history/gps.model';
import { Route } from '@app/shared/models/ride-history/route.model';
import { environment } from '@environments/environment';
import { HttpService } from '../../service/http/http.service';
import { firstValueFrom } from 'rxjs';
import { backendRestApis } from '@environments/backend-rest-apis';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-map',
  imports: [],
  templateUrl: './map.component.html',
  styleUrl: './map.component.css',
})
export class MapComponent implements AfterViewInit, OnDestroy {

  @ViewChild('map') mapContainer !: { nativeElement: HTMLElement }; // Potrebno za incijaliziranje mape, ovo mi je riješenje koje ne izbacuje
  //  error “Map container is already initialized”.... Link: https://designlimbo.com/leaflet-ionic-3-and-map-container-is-already-initialized/
  
  private httpService = inject(HttpService);
  private webSocketService = inject(WebsocketService);
  private gpsSubscription?: Subscription;

  mode = input<'live' | 'route history' | 'route details' | 'geofence'>();

  routeDetailsRouteId = input<number | undefined>(); // For 'route details' mode, this will be the ID of the route to display
  routeDetailsGpsData = signal<[number, number][]>([]);

  routes = input<Route[] | undefined>();
  gpsData = signal<[number, number][]>([]);
  routeId: number[] = [];

  

  private map!: L.Map;
  private marker!: L.Marker;
  icon = {
    icon: L.icon({
      iconSize: [25, 41],
      iconAnchor: [12.5, 41], // Adjusted to align with the bottom center of the icon
      iconUrl: 'leaflet/marker-icon.png',
      shadowUrl: 'leaflet/marker-shadow.png',
    }),
  };

  /**
   * Inicijalizira kartu i postavlja način rada (live, route history, route details).
   */
  ngAfterViewInit(): void {

    this.initMap();

    // Live mode
    if (this.mode() === 'live') {
      this.marker = L.marker([45.8130, 15.9770], this.icon).addTo(this.map);
      this.webSocketService.connect(); // Connect to the WebSocket server

      // Subscribe to GPS data updates
      this.gpsSubscription = this.webSocketService.gpsDataEmitter.subscribe((gpsDataForWs) => {
        this.updateMap(gpsDataForWs);
      });
    }

    // Route history mode
    if (this.mode() === 'route history') {
      if (!this.routes) {
        throw new Error(
          'RouteHistory način zahtijeva predane "routes" podatke.'
        );
      }

      const inputRoutes = this.routes();
      if (inputRoutes) {
        this.routeId = inputRoutes
          .filter((route) => route?.id !== undefined)
          .map((route) => route.id!);

        // Mapiraj svaki route ID u HTTP poziv
        const requests = this.routeId.map((id) =>
          firstValueFrom(
            this.httpService.get<Gps[]>(
              `${environment.apiUrl}/api/v1/gps/routeId/${id}`
            )
          )
        );

        // Pričekaj sve pozive paralelno
        Promise.all(requests).then((allResponses) => {
          const allPoints: [number, number][] = [];

          for (let gpsData of allResponses) {
            if (!gpsData || gpsData.length === 0) return;
            const sorted = gpsData.sort(
              (a, b) =>
                new Date(a.timestamp).getTime() -
                new Date(b.timestamp).getTime()
            );

            const routePoints = sorted.map(
              (g) => [g.latitude, g.longitude] as [number, number]
            );
            allPoints.push(...routePoints);
          }

          // Ažuriraj signal i nacrtaj rutu
          this.gpsData.set(allPoints);
          this.drawRouteOnMap(allPoints);
        });
      }
    }


    // Route details mode
    if(this.mode() === 'route details') {
      if (!this.routeDetailsRouteId()) {
        throw new Error(
          'RouteDetails način zahtijeva predani "routeDetailsRouteId" podatak.'
        );
      }

      const routeId = this.routeDetailsRouteId();
      if (routeId === undefined) return;
    
      this.httpService.get<Gps[]>(`${environment.apiUrl}${backendRestApis.gps.getAllGpsWithRouteId(routeId)}`).subscribe({
        next: (gpsData) => {
          if (!gpsData || gpsData.length === 0) return;
          const sorted = gpsData.sort(
            (a, b) =>
              new Date(a.timestamp).getTime() -
              new Date(b.timestamp).getTime()
          );

          const routePoints = sorted.map(
            (g) => [g.latitude, g.longitude] as [number, number]
          );
          this.routeDetailsGpsData.set(routePoints);
          this.drawRouteOnMap(routePoints);
        },
        error: (error) => {
          console.error('Error fetching GPS data:', error);
        }
      });
    }

  }

  ngOnDestroy(): void {
    this.gpsSubscription?.unsubscribe();
    this.webSocketService.disconnect();
  }


  /**
   * Inicijalizacija mape, postavljanje markera na default lokaciju i dodavanje OpenStreetMap sloja.
   */
  private initMap(): void {
  
    this.map = L.map(this.mapContainer.nativeElement).setView([45.8130, 15.9770], 16);
    

    const tiles = L.tileLayer(
      'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        maxZoom: 19,
        minZoom: 3,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }
    );
    tiles.addTo(this.map);
  }

  /**
   * Ažurira marker i centar karte prema novim GPS podacima (samo za 'live' način).
   * @param gpsData Objekt s latitudom i longitudom
   */
  updateMap(gpsData: { lat: number; lon: number }) {
    if (
      gpsData &&
      typeof gpsData.lat === 'number' &&
      typeof gpsData.lon === 'number'
    ) {
      this.marker.setLatLng([gpsData.lat, gpsData.lon]); // Update marker position
      this.map.panTo([gpsData.lat, gpsData.lon]); // Move the map to the new position without changing zoom
    } else {
      console.error('Invalid GPS data for map update:', gpsData);
    }
  }

  /**
   * Crta rutu na karti prema zadanim točkama.
   * @param points Polje točaka [lat, lon]
   */
  drawRouteOnMap(points: [number, number][]) {
    const polyline = L.polyline(points, { color: '#142edb' }).addTo(this.map);
    this.map.fitBounds(polyline.getBounds());
  }
}
